import { captureException, setExtra } from '@sentry/browser';
import { serialize } from 'object-to-formdata';
import queryString from 'query-string';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import { KeyedMutator } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { useNotification } from 'hooks/useNotification';
import { API_ROUTES } from 'lib/api-routes';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import {
  ImagesAttributes,
  NewsletterAttributes,
  NewsletterCreate,
  NewsletterResponse,
  NewsletterStates,
  NewslettersResponse,
  UploadImageResponse,
} from 'lib/models/newsletter';
import { get, http, post, remove } from 'lib/utils/http';
import { parsePaginatedResponse, parseResponse } from 'lib/utils/parser';

export const NewsletterColumns: {
  id: number;
  field: string;
  label: string;
  width: string;
}[][] = [
  [
    {
      id: 1,
      field: 'subject',
      label: 'Subject',
      width: '35%',
    },
    {
      id: 2,
      field: 'sent_at',
      label: 'Date sent',
      width: '15%',
    },
    {
      id: 3,
      field: '',
      label: 'Audience',
      width: '15%',
    },
    {
      id: 4,
      field: '',
      label: 'Opened',
      width: '15%',
    },
    {
      id: 5,
      field: '',
      label: 'Clicked',
      width: '15%',
    },
    {
      id: 6,
      field: '',
      label: '',
      width: '5%',
    },
  ],
  [
    {
      id: 1,
      field: 'subject',
      label: 'Subject',
      width: '65%',
    },
    {
      id: 3,
      field: '',
      label: 'Audience',
      width: '15%',
    },
    {
      id: 4,
      field: '',
      label: 'Scheduled date',
      width: '15%',
    },
    {
      id: 6,
      field: '',
      label: '',
      width: '5%',
    },
  ],
  [
    {
      id: 1,
      field: 'subject',
      label: 'Subject',
      width: '75%',
    },
    {
      id: 4,
      field: 'created_at',
      label: 'Created date',
      width: '20%',
    },
    {
      id: 6,
      field: '',
      label: '',
      width: '5%',
    },
  ],
];

export enum SocietyTabsType {
  SENT = 0,
  SCHEDULED = 1,
  DRAFTS = 2,
}

export enum ConfirmationScreen {
  NO_SCREEN = 0,
  DELETE = 1,
  CONFIRMATION_SCREEN = 2,
  SEND_NOW = 3,
  CANCEL_SENDING = 4,
}

export interface UseNewsLettersReturnType {
  onDelete: (
    id: string,
    notify?: boolean
  ) => Promise<{ notice: string } | undefined>;
  newslettersList: NewsletterAttributes[];
  deleteImage: (id: string, img: { attributes: ImagesAttributes }) => void;
  uploadImage: (id: string, formData: FormData) => Promise<UploadImageResponse>;
  getNewsletterData: (id: string) => Promise<NewsletterAttributes>;
  size: number;
  setSize: (size: number) => void;
  hasMoreRecords: boolean;
  isLoadingMore: boolean | undefined;
  totalCount: number | undefined;
  countMapping: Record<string, number>;
  isAPILoading: boolean;
  mutateNewsletters: KeyedMutator<NewslettersResponse[]>;
  selectedNewsletterState: SocietyTabsType;
  setSelectedNewsletterState: Dispatch<SetStateAction<SocietyTabsType>>;
  createNewsletter: ({
    news_letter,
  }: {
    news_letter: NewsletterCreate;
  }) => Promise<NewsletterAttributes | undefined>;
  getNewsletterResponse: (pathname: string) => Promise<NewsletterAttributes>;
  updateNewsletter: ({
    news_letter,
    id,
  }: {
    news_letter: NewsletterCreate;
    id: string;
  }) => Promise<NewsletterAttributes | undefined>;
  getState: (state: NewsletterStates) => 'Sent' | 'Draft' | 'Scheduled';
  copyToDraftNewsletter: (newsletter_id: string) => Promise<string | undefined>;
}

const PER_PAGE_COUNT = 10;

export const useNewsletters = (): UseNewsLettersReturnType => {
  const notificationInstance = useNotification();
  const { headers, plainHeaders } = useRecoilValue(httpHeadersState);

  const [selectedNewsletterState, setSelectedNewsletterState] =
    useState<SocietyTabsType>(0);

  const getSelectedNewsletterStateSociety = useCallback(() => {
    switch (selectedNewsletterState) {
      case SocietyTabsType.SENT:
        return ['sent'];
      case SocietyTabsType.DRAFTS:
        return ['draft'];
      case SocietyTabsType.SCHEDULED:
        return ['scheduled'];
      default:
    }
  }, [selectedNewsletterState]);

  const getJobsKey = (pageIndex: number) => {
    const params = {
      page: pageIndex + 1,
      per_page: PER_PAGE_COUNT,
      ...([
        SocietyTabsType.SENT,
        SocietyTabsType.DRAFTS,
        SocietyTabsType.SCHEDULED,
      ].includes(selectedNewsletterState) && {
        state: getSelectedNewsletterStateSociety(),
      }),
    };
    const qs = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });
    let sortBy = 'created_at';
    if (selectedNewsletterState === SocietyTabsType.SENT) {
      sortBy = 'sent_at';
    }
    if (selectedNewsletterState === SocietyTabsType.SCHEDULED) {
      sortBy = 'scheduled_at';
    }
    const pathname = `${API_ROUTES.SOCIETY_NEWSLETTERS}?${qs}&sort_by=${sortBy}&sort_order=desc`;
    return [pathname, headers];
  };

  const {
    data: newslettersResponse,
    error: newslettersError,
    size,
    setSize,
    mutate: mutateNewsletters,
  } = useSWRInfinite<NewslettersResponse>(getJobsKey, get, {
    revalidateOnFocus: true,
    revalidateFirstPage: true,
  });

  const isAPILoading = !newslettersResponse && !newslettersError;
  const isLoadingMore =
    isAPILoading ||
    (size > 0 &&
      newslettersResponse &&
      typeof newslettersResponse[size - 1] === 'undefined');

  const newslettersList = useMemo(() => {
    return parsePaginatedResponse<NewsletterAttributes>(newslettersResponse);
  }, [selectedNewsletterState, newslettersResponse]);

  const totalCount = useMemo(() => {
    return newslettersResponse
      ? newslettersResponse[0]?.meta?.total
      : undefined;
  }, [newslettersResponse]);

  const countMapping = newslettersResponse
    ? newslettersResponse[0]?.meta.count_mapping
    : {};

  const hasMoreRecords = useMemo(() => {
    const firstApplicationList = Array.isArray(newslettersResponse)
      ? newslettersResponse[0]
      : null;
    if (firstApplicationList) {
      return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
    } else {
      return false;
    }
  }, [newslettersResponse, size]);

  const onDelete = async (
    id: string,
    notify = true
  ): Promise<{ notice: string } | undefined> => {
    const path = `${API_ROUTES.SOCIETY_NEWSLETTERS}/${id}`;

    try {
      const response = await remove<{ notice: string }>(path, headers);
      mutateNewsletters();
      if (notify)
        notificationInstance.success({
          title: 'Success!',
          message: 'Newsletter removed successfully.',
        });
      return response;
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const deleteImage = async (
    id: string,
    img: { attributes: ImagesAttributes }
  ) => {
    const { NEXT_PUBLIC_API_HOST_URL } = process.env;
    const deleteUrl =
      `${NEXT_PUBLIC_API_HOST_URL}${API_ROUTES.DELETE_NEWSLETTER_IMAGE}`.replace(
        '[id]',
        id || ''
      );
    const body = {
      image_id: img.attributes.id,
    };
    await fetch(deleteUrl, {
      method: 'DELETE',
      headers: headers,
      body: JSON.stringify(body),
    });
  };
  const uploadImage = async (id: string, formData: FormData) => {
    const { NEXT_PUBLIC_API_HOST_URL } = process.env;

    const pathname =
      `${NEXT_PUBLIC_API_HOST_URL}${API_ROUTES.UPLOAD_NEWSLETTER_IMAGE}`.replace(
        '[id]',
        id
      );

    const response = await fetch(pathname, {
      method: 'POST',
      body: formData,
      headers: plainHeaders,
    });

    const data = await response.json();
    return data;
  };

  const getNewsletterData = async (id: string) => {
    const response = await get<NewsletterResponse>(
      `${API_ROUTES.SOCIETY_NEWSLETTERS}/${id}`,
      headers
    );

    return parseResponse(response);
  };

  const createNewsletter = async ({
    news_letter,
  }: {
    news_letter: NewsletterCreate;
  }) => {
    const body = {
      news_letter,
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<NewsletterResponse>(
        API_ROUTES.SOCIETY_NEWSLETTERS,
        {
          method: 'POST',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('createEvent body', JSON.stringify(body));
      captureException(error);
    }
  };

  const copyToDraftNewsletter = async (newsletter_id: string) => {
    const pathname = API_ROUTES.DUPLICATE_NEWSLETTER.replace(
      '[id]',
      newsletter_id
    );
    const response = await post<NewsletterResponse>(pathname, {}, headers);
    if (response) {
      return parseResponse(response).id;
    }
  };

  async function getNewsletterResponse(
    pathname: string
  ): Promise<NewsletterAttributes> {
    const response = await get<NewsletterResponse>(pathname, headers);
    const newsletter = parseResponse<NewsletterAttributes>(response);

    return newsletter;
  }

  const updateNewsletter = async ({
    news_letter,
    id,
  }: {
    news_letter?: NewsletterCreate;
    id: string;
  }) => {
    const body = {
      news_letter,
    };

    const formData = serialize(body, {
      indices: false,
      allowEmptyArrays: true,
    });
    try {
      const response = await http<NewsletterResponse>(
        `${API_ROUTES.SOCIETY_NEWSLETTERS}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateNewsletter body', JSON.stringify(body));
      captureException(error);
    }
  };

  const getState = (state: NewsletterStates) => {
    if (state === NewsletterStates.SENT) return 'Sent';
    else if (state === NewsletterStates.DRAFT) return 'Draft';
    else if (state === NewsletterStates.SCHEDULED) return 'Scheduled';
    else return state;
  };

  return {
    onDelete,
    getNewsletterData,
    uploadImage,
    deleteImage,
    newslettersList,
    size,
    setSize,
    hasMoreRecords,
    isLoadingMore,
    totalCount,
    countMapping,
    isAPILoading,
    mutateNewsletters,
    selectedNewsletterState,
    setSelectedNewsletterState,
    createNewsletter,
    getNewsletterResponse,
    updateNewsletter,
    getState,
    copyToDraftNewsletter,
  };
};
